import { type FormEvent, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/spinner';
import { cn } from '@/lib/cn';

/**
 * FormDialog — the canonical "open a modal, fill a form, submit" pattern
 * used by every Create/Edit dialog in the app. Standardizes:
 *   - Header (title + description + optional icon)
 *   - Form scaffolding
 *   - Footer (Cancel + primary)
 *   - Loading state on the submit button
 *
 *   <FormDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     trigger={<Button>New</Button>}
 *     title="New project"
 *     description="…"
 *     submitLabel="Create"
 *     submitting={create.isPending}
 *     onSubmit={handleSubmit(values => …)}
 *   >
 *     <FormField label="Name">…</FormField>
 *   </FormDialog>
 */
export interface FormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  submitting?: boolean;
  submitDisabled?: boolean;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void | Promise<void>;
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  footer?: ReactNode;
}

const SIZE_CLASS = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-xl',
} as const;

export function FormDialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  icon,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  submitting = false,
  submitDisabled = false,
  onSubmit,
  size = 'md',
  children,
  footer,
}: FormDialogProps): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent className={cn(SIZE_CLASS[size])}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {icon}
            {title}
          </DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>

        <form className="space-y-4" noValidate onSubmit={onSubmit}>
          {children}

          {footer ?? (
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                {cancelLabel}
              </Button>
              <Button type="submit" disabled={submitting || submitDisabled}>
                {submitting ? <Spinner className="mr-2" /> : null}
                {submitLabel}
              </Button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Empty-state content for dialogs that can't proceed (e.g. no projects).
 */
export function FormDialogBlock({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  closeLabel = 'Got it',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  closeLabel?: string;
}): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            {closeLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
